"use client";

import { Download, Lock, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { downloadTextFile } from "@/components/bibliography-export-menu";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createBibliographyBackup,
  restoreBibliographyBackup,
} from "@/lib/bibliography";

/**
 * The sidebar footer: a storage status line plus whole-workspace backup and restore.
 * Kept away from the per-project export menu because these act on every project at
 * once and one of them is destructive.
 */
export function BackupControls({ onRestored }: { onRestored: () => void }) {
  const bib = useBibStrings();
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreText, setRestoreText] = useState("");

  function handleBackup() {
    const date = new Date().toISOString().slice(0, 10);
    downloadTextFile(
      createBibliographyBackup(),
      "application/json",
      "json",
      `citationeasy-backup-${date}`
    );
    toast.success(bib.backupDownloaded);
  }

  function handleRestoreFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setRestoreText(reader.result);
      }
    };
    reader.onerror = () => toast.error(bib.backupUnreadable);
    reader.readAsText(file);
    event.target.value = "";
  }

  function handleRestoreSubmit(event: React.FormEvent) {
    event.preventDefault();
    try {
      restoreBibliographyBackup(restoreText);
      setRestoreText("");
      setRestoreOpen(false);
      onRestored();
      toast.success(bib.restoreComplete);
    } catch {
      toast.error(bib.restoreInvalid);
    }
  }

  return (
    <div className="space-y-2 border-border/70 border-t px-3 py-3">
      <p className="flex items-center gap-1.5 text-muted-foreground text-xs">
        <Lock className="size-3" />
        {bib.studioSavedLocally}
      </p>
      <div className="flex gap-1">
        <Button
          className="flex-1 justify-start"
          onClick={handleBackup}
          size="xs"
          title={bib.backupAll}
          variant="ghost"
        >
          <Download className="size-3.5" />
          {bib.studioBackup}
        </Button>
        <Button
          className="flex-1 justify-start"
          onClick={() => setRestoreOpen(true)}
          size="xs"
          title={bib.restoreTitle}
          variant="ghost"
        >
          <Upload className="size-3.5" />
          {bib.restore}
        </Button>
      </div>

      <Dialog
        onOpenChange={(next) => {
          setRestoreOpen(next);
          if (!next) {
            setRestoreText("");
          }
        }}
        open={restoreOpen}
      >
        <DialogContent className="sm:max-w-lg" showCloseButton={true}>
          <DialogHeader>
            <DialogTitle>{bib.restoreTitle}</DialogTitle>
            <DialogDescription>{bib.restoreWarning}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            id="restore-project-form"
            onSubmit={handleRestoreSubmit}
          >
            <div className="space-y-2">
              <Label htmlFor="restore-project-file">{bib.restoreChoose}</Label>
              <Input
                accept=".json,application/json"
                id="restore-project-file"
                onChange={handleRestoreFile}
                type="file"
              />
            </div>
          </form>
          <DialogFooter>
            <Button
              onClick={() => setRestoreOpen(false)}
              type="button"
              variant="outline"
            >
              {bib.cancel}
            </Button>
            <Button
              disabled={!restoreText}
              form="restore-project-form"
              type="submit"
              variant="destructive"
            >
              {bib.replaceAndRestore}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
